import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { performanceApi } from '../../api/performance';
import { employeeApi } from '../../api/hr';
import { ApiError } from '../../api/client';
import { useToast } from '../../components/Toast';
import { ReturnAction } from '../../components/ReturnAction';
import { ReturnedBanner } from '../../components/ReturnedBanner';
import { usePermission } from '../../auth/AuthContext';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkflowStepper } from '../../components/WorkflowStepper';

const APPRAISAL_STEPS = [
  { key: 'opened', label: 'Opened' },
  { key: 'self_assessment', label: 'Self-Assessment' },
  { key: 'supervisor_assessment', label: 'Supervisor Assessment' },
  { key: 'final_rating', label: 'Final Rating (HR)' },
  { key: 'approved', label: 'Approved' },
];

const STATUS_LABELS: Record<string, string> = {
  opened: 'Opened', self_assessment: 'Self-Assessment', supervisor_assessment: 'Supervisor Assessment',
  returned: 'Returned for Redraft', final_rating: 'Final Rating (HR)', approved: 'Approved', cancelled: 'Cancelled',
};

/**
 * One shared page, not three near-duplicate ones - it adapts to
 * whoever is viewing it. isMe/isSupervisor are determined by
 * comparing the appraisal's employee_id/supervisor_id against the
 * viewer's own employee record, the same person-specific pattern
 * already enforced server-side.
 */
export function AppraisalDetailPage() {
  const { appraisalId } = useParams<{ appraisalId: string }>();
  const id = Number(appraisalId);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const isHR = usePermission('performance.manage');

  const appraisalQuery = useQuery({ queryKey: ['appraisal', id], queryFn: () => performanceApi.get(id) });
  const meQuery = useQuery({ queryKey: ['my-profile'], queryFn: () => employeeApi.getMe() });

  const [objectiveForm, setObjectiveForm] = useState({ objective: '', weight: '', target: '' });
  const [selfRatings, setSelfRatings] = useState<Record<number, { achievement: string; rating: string }>>({});
  const [selfCompetencyRatings, setSelfCompetencyRatings] = useState<Record<number, string>>({});
  const [supervisorRatings, setSupervisorRatings] = useState<Record<number, { achievement: string; rating: string; comments: string }>>({});
  const [supervisorCompetencyRatings, setSupervisorCompetencyRatings] = useState<Record<number, { rating: string; comments: string }>>({});
  const [finalRating, setFinalRating] = useState('');
  const [developmentPlan, setDevelopmentPlan] = useState('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['appraisal', id] });

  const addObjectiveMutation = useMutation({
    mutationFn: () => performanceApi.addObjective(id, { objective: objectiveForm.objective, weight: objectiveForm.weight ? Number(objectiveForm.weight) : undefined, target: objectiveForm.target || undefined }),
    onSuccess: () => { showSuccess('Objective added.'); invalidate(); setObjectiveForm({ objective: '', weight: '', target: '' }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const openMutation = useMutation({
    mutationFn: () => performanceApi.openForSelfAssessment(id),
    onSuccess: () => { showSuccess('Opened for self-assessment.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const submitSelfMutation = useMutation({
    mutationFn: () => performanceApi.submitSelfAssessment(id, {
      objectives: Object.entries(selfRatings).map(([objectiveId, v]) => ({ objectiveId: Number(objectiveId), achievement: v.achievement, rating: v.rating ? Number(v.rating) : undefined })),
      competencies: Object.entries(selfCompetencyRatings).map(([ratingId, rating]) => ({ ratingId: Number(ratingId), rating: Number(rating) })),
    }),
    onSuccess: () => { showSuccess('Self-assessment submitted.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const submitSupervisorMutation = useMutation({
    mutationFn: () => performanceApi.submitSupervisorAssessment(id, {
      objectives: Object.entries(supervisorRatings).map(([objectiveId, v]) => ({ objectiveId: Number(objectiveId), achievement: v.achievement, rating: v.rating ? Number(v.rating) : undefined, comments: v.comments || undefined })),
      competencies: Object.entries(supervisorCompetencyRatings).map(([ratingId, v]) => ({ ratingId: Number(ratingId), rating: Number(v.rating), comments: v.comments || undefined })),
    }),
    onSuccess: () => { showSuccess('Supervisor assessment submitted.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const saveRatingMutation = useMutation({
    mutationFn: () => performanceApi.setFinalRating(id, { finalRating, developmentPlan: developmentPlan || undefined }),
    onSuccess: () => { showSuccess('Final rating saved.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const approveMutation = useMutation({
    mutationFn: () => performanceApi.approve(id),
    onSuccess: () => { showSuccess('Appraisal approved.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  if (!appraisalQuery.data || !meQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;

  const { data: appraisal, objectives, competencyRatings } = appraisalQuery.data;
  const myEmployeeId = meQuery.data.data.id;
  const isMe = appraisal.employee_id === myEmployeeId;
  const isSupervisor = appraisal.supervisor_id === myEmployeeId;

  return (
    <div className="page">
      <div className="page-header"><h1>{appraisal.cycle_name}</h1></div>
      <p className="muted">{appraisal.employee_name ?? ''} · <StatusBadge status={appraisal.status} label={STATUS_LABELS[appraisal.status]} /></p>

      <section className="card">
        <WorkflowStepper
          steps={APPRAISAL_STEPS}
          currentStatus={appraisal.status}
          detourLabel={appraisal.status === 'returned' ? 'Returned for Redraft' : appraisal.status === 'cancelled' ? 'Cancelled' : undefined}
          reachedIndex={appraisal.status === 'returned' ? 2 : undefined}
        />
      </section>

      {appraisal.status === 'opened' && isHR && (
        <section className="card">
          <h2>Objectives</h2>
          <div className="inline-form">
            <label>Objective<input value={objectiveForm.objective} onChange={(e) => setObjectiveForm((f) => ({ ...f, objective: e.target.value }))} /></label>
            <label>Weight (%)<input type="number" value={objectiveForm.weight} onChange={(e) => setObjectiveForm((f) => ({ ...f, weight: e.target.value }))} /></label>
            <label>Target<input value={objectiveForm.target} onChange={(e) => setObjectiveForm((f) => ({ ...f, target: e.target.value }))} /></label>
            <button onClick={() => addObjectiveMutation.mutate()} disabled={!objectiveForm.objective || addObjectiveMutation.isPending}>Add Objective</button>
          </div>
          {objectives.length > 0 && <button onClick={() => openMutation.mutate()} disabled={openMutation.isPending}>Open for Self-Assessment</button>}
        </section>
      )}

      <section className="card">
        <h2>Objectives &amp; Ratings</h2>
        {objectives.map((o) => (
          <div key={o.id} className="objective-row">
            <p><strong>{o.objective}</strong>{o.target && <span className="muted"> - Target: {o.target}</span>}</p>
            {appraisal.status === 'self_assessment' && isMe ? (
              <div className="inline-form">
                <label>Achievement<input value={selfRatings[o.id]?.achievement ?? ''} onChange={(e) => setSelfRatings((s) => ({ ...s, [o.id]: { ...s[o.id], achievement: e.target.value } }))} /></label>
                <label>Self Rating (1-5)<input type="number" step="0.5" value={selfRatings[o.id]?.rating ?? ''} onChange={(e) => setSelfRatings((s) => ({ ...s, [o.id]: { ...s[o.id], rating: e.target.value } }))} /></label>
              </div>
            ) : (
              o.self_achievement && <p className="muted">Self: {o.self_achievement} (Rating: {o.self_rating ?? '-'})</p>
            )}
            {appraisal.status === 'supervisor_assessment' && isSupervisor ? (
              <div className="inline-form">
                <label>Assessment<input value={supervisorRatings[o.id]?.achievement ?? ''} onChange={(e) => setSupervisorRatings((s) => ({ ...s, [o.id]: { ...s[o.id], achievement: e.target.value, comments: s[o.id]?.comments ?? '' } }))} /></label>
                <label>Supervisor Rating (1-5)<input type="number" step="0.5" value={supervisorRatings[o.id]?.rating ?? ''} onChange={(e) => setSupervisorRatings((s) => ({ ...s, [o.id]: { ...s[o.id], rating: e.target.value, achievement: s[o.id]?.achievement ?? '' } }))} /></label>
              </div>
            ) : (
              o.supervisor_achievement && <p className="muted">Supervisor: {o.supervisor_achievement} (Rating: {o.supervisor_rating ?? '-'})</p>
            )}
          </div>
        ))}
      </section>

      <section className="card">
        <h2>Competencies</h2>
        {competencyRatings.map((c) => (
          <div key={c.id} className="objective-row">
            <p><strong>{c.competency_name}</strong></p>
            {appraisal.status === 'self_assessment' && isMe && (
              <label>Self Rating (1-5)<input type="number" step="0.5" value={selfCompetencyRatings[c.id] ?? ''} onChange={(e) => setSelfCompetencyRatings((s) => ({ ...s, [c.id]: e.target.value }))} /></label>
            )}
            {appraisal.status === 'supervisor_assessment' && isSupervisor && (
              <label>Supervisor Rating (1-5)<input type="number" step="0.5" value={supervisorCompetencyRatings[c.id]?.rating ?? ''} onChange={(e) => setSupervisorCompetencyRatings((s) => ({ ...s, [c.id]: { ...s[c.id], rating: e.target.value } }))} /></label>
            )}
            {(c.self_rating || c.supervisor_rating) && <p className="muted">Self: {c.self_rating ?? '-'} · Supervisor: {c.supervisor_rating ?? '-'}</p>}
          </div>
        ))}
      </section>

      {appraisal.status === 'self_assessment' && isMe && (
        <button onClick={() => submitSelfMutation.mutate()} disabled={submitSelfMutation.isPending}>Submit Self-Assessment</button>
      )}
      {appraisal.status === 'supervisor_assessment' && isSupervisor && (
        <span className="row-actions">
          <button onClick={() => submitSupervisorMutation.mutate()} disabled={submitSupervisorMutation.isPending}>Submit Supervisor Assessment</button>
          <ReturnAction onReturn={(comment) => performanceApi.return(id, comment)} queryKeysToInvalidate={[['appraisal', id]]} />
        </span>
      )}

      {appraisal.status === 'returned' && isMe && (
        <ReturnedBanner returnComment={appraisal.return_comment} resubmit={() => performanceApi.resubmit(id)} queryKeysToInvalidate={[['appraisal', id]]} />
      )}

      {appraisal.status === 'final_rating' && isHR && (
        <section className="card">
          <h2>Final Rating</h2>
          <label>Final Rating<input value={finalRating} onChange={(e) => setFinalRating(e.target.value)} placeholder="e.g. Exceeds Expectations" /></label>
          <label>Development Plan<textarea rows={3} value={developmentPlan} onChange={(e) => setDevelopmentPlan(e.target.value)} /></label>
          <div className="form-actions">
            <button onClick={() => saveRatingMutation.mutate()} disabled={!finalRating || saveRatingMutation.isPending}>Save Rating</button>
            <button onClick={() => approveMutation.mutate()} disabled={!appraisal.final_rating || approveMutation.isPending}>Approve</button>
          </div>
        </section>
      )}

      {appraisal.status === 'approved' && (
        <section className="card">
          <h2>Final Outcome</h2>
          <p><strong>{appraisal.final_rating}</strong></p>
          {appraisal.development_plan && <p className="muted">{appraisal.development_plan}</p>}
        </section>
      )}
    </div>
  );
}
